import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAssignments } from 'entities/assignments';
import { useTaughtLessons } from 'entities/taught-lessons';
import { useAllClassesData } from 'entities/classes';
import { useFilters, useSelectedClassKey } from 'features/filters';
import { useAssignmentsQueryInvalidation } from 'features/assignment-panel';
import { Assignment } from 'shared/api/assignment-service';
import { TaughtLesson } from 'shared/api/class-stats';
import { useInterval } from 'shared/lib/utils';
import { ERROR_MESSAGE } from 'shared/config/constants';

import { sortStreamDataByStartDateDesc } from '../utils';
import {
  MAIN_PAGE_INIT_ITEMS_COUNT,
  MAIN_PAGE_ITEMS_LIMIT_PER_QUERY,
  STREAM_ITEMS_LIMIT_PER_QUERY,
  STREAM_CONTENT_REFRESH_INTERVAL,
} from '../../constants';
import { QueryArguments } from '../../types';

export const useStreamContentData = (enableInfiniteScroll = false) => {
  const initialItemsQueryLimit = enableInfiniteScroll
    ? STREAM_ITEMS_LIMIT_PER_QUERY
    : MAIN_PAGE_ITEMS_LIMIT_PER_QUERY;

  const initialQueryArgs = {
    offset: 0,
    limit: initialItemsQueryLimit,
    selectedClass: [],
    subjects: [],
  };

  const [isFetchingOnClassChange, setIsFetchingOnClassChange] = useState(false);
  const [assignmentsQueryArgs, setAssignmentsQueryArgs] =
    useState<QueryArguments>(initialQueryArgs);
  const [taughtLessonsQueryArgs, setTaughtLessonsQueryArgs] =
    useState<QueryArguments>(initialQueryArgs);
  const [itemsToDisplay, setItemsToDisplay] = useState<
    (Assignment | TaughtLesson)[] | null
  >(null);
  const [shouldShowSeeAllButton, setShouldShowSeeAllButton] = useState(false);

  const { allClassesKeys } = useAllClassesData();
  const selectedClassKey = useSelectedClassKey();
  const prevSelectedClassKey = useRef(selectedClassKey);

  const selectedClass =
    enableInfiniteScroll && allClassesKeys.length > 1
      ? selectedClassKey
      : allClassesKeys;

  const filters = useFilters();

  const selectedSubjects = useMemo(
    () => (enableInfiniteScroll ? filters.selectedSubjects : []),
    [enableInfiniteScroll, filters.selectedSubjects],
  );

  // Set initial arguments values for 'selectedClass' and 'subjects'.
  useEffect(() => {
    setAssignmentsQueryArgs((prev) => ({
      ...prev,
      selectedClass,
      subjects: selectedSubjects,
    }));
    setTaughtLessonsQueryArgs((prev) => ({
      ...prev,
      selectedClass,
      subjects: selectedSubjects,
    }));
  }, [
    selectedClass,
    filters.selectedSubjects,
    enableInfiniteScroll,
    assignmentsQueryArgs.offset,
    taughtLessonsQueryArgs.offset,
    selectedSubjects,
  ]);

  const {
    data: assignments,
    isAllDataFetched: isAllAssignmentsFetched,
    isLoading: isAssignmentsLoading,
    isFetching: isAssignmentsFetching,
    isError: isAssignmentsError,
    refetch: refetchAssignments,
    assignmentsTotalCount,
  } = useAssignments(assignmentsQueryArgs);

  const {
    data: taughtLessons,
    isAllDataFetched: isAllTaughtLessonsFetched,
    isLoading: isTaughtLessonsLoading,
    isFetching: isTaughtLessonsFetching,
    isError: isTaughtLessonsError,
    refetch: refetchTaughtLessons,
    taughtLessonsTotalCount,
  } = useTaughtLessons(taughtLessonsQueryArgs);

  const isLoading = isAssignmentsLoading || isTaughtLessonsLoading;
  const isFetching = isAssignmentsFetching || isTaughtLessonsFetching;
  const isError = isAssignmentsError || isTaughtLessonsError;

  const handleAssignmentsRefresh = () => {
    // Update limit and offset parameters if needed to refetch all previously fetched items.
    setAssignmentsQueryArgs((prevState) => {
      if (
        prevState.limit === prevState.limit + prevState.offset &&
        prevState.offset === 0
      ) {
        return prevState;
      }

      return {
        ...prevState,
        limit: prevState.limit + prevState.offset,
        offset: 0,
      };
    });

    // trigger fetching manually if query args won't be changed
    refetchAssignments();
  };

  const handleTaughtLessonsRefresh = () => {
    setTaughtLessonsQueryArgs((prevState) => {
      if (
        prevState.limit === prevState.limit + prevState.offset &&
        prevState.offset === 0
      ) {
        return prevState;
      }

      return {
        ...prevState,
        limit: prevState.limit + prevState.offset,
        offset: 0,
      };
    });

    // trigger fetching manually if query args won't be changed
    refetchTaughtLessons();
  };

  // Update queryArgs states every 30 seconds that will trigger regular refresh of all previously fetched items.
  const handleStreamContentRefresh = () => {
    handleAssignmentsRefresh();
    handleTaughtLessonsRefresh();
  };

  useAssignmentsQueryInvalidation(handleStreamContentRefresh);

  useInterval({
    callback: handleTaughtLessonsRefresh,
    ms: STREAM_CONTENT_REFRESH_INTERVAL,
    skip: !selectedClass.length,
  });

  useEffect(() => {
    if (!assignments || !taughtLessons) return;

    const expectedAmountOfFetchedItems =
      assignmentsQueryArgs.limit +
      assignmentsQueryArgs.offset +
      taughtLessonsQueryArgs.limit +
      taughtLessonsQueryArgs.offset;
    const fetchedItemsCount = assignmentsTotalCount + taughtLessonsTotalCount;

    // Don't update 'itemsToDisplay' if not all items have been received yet.
    if (
      fetchedItemsCount < expectedAmountOfFetchedItems &&
      enableInfiniteScroll &&
      !isAllTaughtLessonsFetched &&
      !isAllAssignmentsFetched &&
      !selectedSubjects.length
    ) {
      return;
    }

    const sortedData = sortStreamDataByStartDateDesc(
      assignments,
      taughtLessons,
    );

    // Show a limited number of items without an infinite scroll on a preview stream mode.
    if (!enableInfiniteScroll && sortedData) {
      setItemsToDisplay(sortedData.slice(0, MAIN_PAGE_INIT_ITEMS_COUNT));

      if (sortedData.length > MAIN_PAGE_INIT_ITEMS_COUNT) {
        setShouldShowSeeAllButton(true);
      }

      return;
    }

    setItemsToDisplay(sortedData);
  }, [
    assignments,
    taughtLessons,
    enableInfiniteScroll,
    isFetching,
    assignmentsQueryArgs.limit,
    assignmentsQueryArgs.offset,
    taughtLessonsQueryArgs.limit,
    taughtLessonsQueryArgs.offset,
    isAllTaughtLessonsFetched,
    isAllAssignmentsFetched,
    selectedSubjects,
    assignmentsTotalCount,
    taughtLessonsTotalCount,
  ]);

  // trigger render of generic error screen on stream page in case of request error
  useEffect(() => {
    if (!enableInfiniteScroll || !isError) return;

    throw new Error(ERROR_MESSAGE.ASSIGNMENTS_ERROR);
  }, [enableInfiniteScroll, isError]);

  const incrementLimitOffset = useCallback(() => {
    // Disable in preview stream mode.
    if (!enableInfiniteScroll) return;

    if (!isAllAssignmentsFetched) {
      setAssignmentsQueryArgs((prevState) => {
        return {
          ...prevState,
          offset: prevState.offset + prevState.limit,
          limit: STREAM_ITEMS_LIMIT_PER_QUERY,
        };
      });
    }

    if (!isAllTaughtLessonsFetched) {
      setTaughtLessonsQueryArgs((prevState) => ({
        ...prevState,
        offset: prevState.offset + prevState.limit,
        limit: STREAM_ITEMS_LIMIT_PER_QUERY,
      }));
    }
  }, [
    enableInfiniteScroll,
    isAllAssignmentsFetched,
    isAllTaughtLessonsFetched,
  ]);

  // Reset limit/offset parameters to initial ones on class change, skip state reset if they are already set as initial ones.
  useEffect(() => {
    const hasClassChanged =
      prevSelectedClassKey.current.length &&
      selectedClassKey.join('') !== prevSelectedClassKey.current.join('');

    if (hasClassChanged) {
      setAssignmentsQueryArgs((prevState) => {
        if (
          prevState.limit === STREAM_ITEMS_LIMIT_PER_QUERY &&
          prevState.offset === 0
        ) {
          return prevState;
        }

        return {
          ...prevState,
          limit: STREAM_ITEMS_LIMIT_PER_QUERY,
          offset: 0,
        };
      });

      setTaughtLessonsQueryArgs((prevState) => {
        if (
          prevState.limit === STREAM_ITEMS_LIMIT_PER_QUERY &&
          prevState.offset === 0
        ) {
          return prevState;
        }

        return {
          ...prevState,
          limit: STREAM_ITEMS_LIMIT_PER_QUERY,
          offset: 0,
        };
      });
    }

    setIsFetchingOnClassChange(Boolean(isFetching && hasClassChanged));

    prevSelectedClassKey.current = selectedClassKey;
  }, [selectedClassKey, isFetching]);

  return {
    itemsToDisplay,
    isLoading,
    isFetchingOnClassChange,
    incrementLimitOffset,
    isFetching,
    selectedClass,
    isAllTaughtLessonsFetched,
    isAllAssignmentsFetched,
    selectedSubjects,
    shouldShowSeeAllButton,
    refetchAssignments: handleAssignmentsRefresh,
    currentClassesKeys: assignmentsQueryArgs.selectedClass,
  };
};
